import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment/moment';

import { useTranslation } from 'react-i18next';

import { Modal, Button, Card, RadioLabel, ToggleLabel, Section, Date, InputLabel, Message, ToastContext } from '@weezevent/nacre';
import RedditonModel from '../../models/redditionModel';
import './redditionModal.scss';
import useAppContext from '../../context/useAppContext';
import i18n from 'i18next';

export default function RedditionModal({ event, onClose, closeEventById }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const locale = i18n.language;

    const [isTemporaryReport, setIsTemporaryReport] = React.useState(true);
    const [toggelDate, setToggelDate] = React.useState(false);
    const [dateStart, setDateStart] = React.useState();
    const [dateEnd, setDateEnd] = React.useState();

    const waitForDownload = React.useCallback(
        uuid => {
            new RedditonModel({}).getInfo({ uuid, organizationId: organization.pk, eventId: event.pk }).then(data => {
                if (data.data_info.ready) {
                    var link = document.createElement('a');
                    link.href = `${window.weezConfig.temporary.menhirUrl}/downloadFile?${uuid}`;
                    link.target = '_blank';
                    link.click();
                    toast.success(t('ticket.accounting.modal.downloaded'));
                    if (!isTemporaryReport) {
                        closeEventById(event.pk);
                    }
                } else {
                    setTimeout(() => {
                        waitForDownload(uuid);
                    }, 2000);
                }
            });
        },
        [organization, event, isTemporaryReport, toast, closeEventById, t]
    );

    const download = React.useCallback(() => {
        const query = { hideCommission: '0', hideFees: '0' };

        toast.info(t('ticket.accounting.modal.downloading'));

        if (isTemporaryReport) {
            if (toggelDate) {
                if (dateStart) {
                    query.dateStart = moment(dateStart).format('YYYY-MM-DD');
                }
                if (dateEnd) {
                    query.dateEnd = moment(dateEnd).format('YYYY-MM-DD');
                }
            }
        }

        onClose();

        const model = new RedditonModel({}, query);

        // Determine the appropriate API call based on the report type (temporary or definitive)
        const fetchUrl = isTemporaryReport
            ? model.getUrlTmp({ organizationId: organization.pk, eventId: event.pk }, query)
            : model.getUrl({ organizationId: organization.pk, eventId: event.pk });

        fetchUrl
            .then(response => {
                waitForDownload(response.data_url.uuid, model);
            })
            .catch(error => {
                console.error(error);
                toast.error(t('ticket.accounting.modal.downloadError'));
            });
    }, [toast, t, isTemporaryReport, toggelDate, dateStart, dateEnd, onClose, waitForDownload, organization, event]);

    React.useEffect(() => {
        if (event.publication_status === 'closed') {
            setIsTemporaryReport(false);
            download();
        }
    }, []);

    return (
        <Modal
            open={Boolean(event && event.publication_status !== 'closed')}
            onClose={onClose}
            title={t('ticket.accounting.modal.title')}
            submitButton={
                <Button
                    primary
                    onClick={() => {
                        download();
                    }}
                    label={isTemporaryReport ? t('ticket.common.action.download') : t('ticket.accounting.modal.encloseAndDownload')}
                />
            }
            cancelButton={<Button onClick={onClose} label={t('ticket.common.action.cancel')} />}
        >
            {
                <Card.Content className="contentCard">
                    <RadioLabel
                        name="isTemporaryReport"
                        checked={isTemporaryReport}
                        inline
                        bordered
                        onClick={() => setIsTemporaryReport(true)}
                        label={t('ticket.accounting.modal.tmpSurrender')}
                    />
                    <RadioLabel
                        name="isNotTmp"
                        checked={!isTemporaryReport}
                        inline
                        bordered
                        onClick={() => {
                            setIsTemporaryReport(false);
                            setToggelDate(false);
                            setDateStart(undefined);
                            setDateEnd(undefined);
                        }}
                        label={t('ticket.accounting.modal.finalySurrender')}
                    />
                    {isTemporaryReport ? (
                        <>
                            <ToggleLabel
                                label={t('ticket.accounting.modal.toggelLabel')}
                                checked={toggelDate}
                                onChange={e => {
                                    setToggelDate(!toggelDate);
                                    if (!toggelDate) {
                                        setDateStart(undefined);
                                        setDateEnd(undefined);
                                    }
                                }}
                                inlinePosition="left"
                            />
                            {toggelDate && (
                                <Section>
                                    <div>
                                        <InputLabel label={t('ticket.accounting.modal.startDate')} />
                                        <Date.Input
                                            onChange={e => {
                                                setDateStart(new Date(e));
                                            }}
                                            value={dateStart}
                                            locale={locale}
                                            minYear={2000}
                                            maxYear={2040}
                                        />
                                    </div>
                                    <div className="inputEndDate">
                                        <InputLabel label={t('ticket.accounting.modal.endDate')} />
                                        <Date.Input
                                            onChange={e => {
                                                setDateEnd(new Date(e));
                                            }}
                                            value={dateEnd}
                                            locale={locale}
                                            minYear={2000}
                                            maxYear={2040}
                                        />
                                    </div>
                                    <div className="contentTooltip">{t('ticket.accounting.modal.timeZone')}</div>
                                </Section>
                            )}
                        </>
                    ) : (
                        <Message title={t('ticket.accounting.modal.msgTitle')} type="warning">
                            {t('ticket.accounting.modal.msg')}
                        </Message>
                    )}
                </Card.Content>
            }
        </Modal>
    );
}

RedditionModal.propTypes = {
    event: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    closeEventById: PropTypes.func.isRequired
};
